import { c as _c } from "react/compiler-runtime";
import './sponsorship-card.scss';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { BadgeProps } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { LinkCard } from '@sola/ark-core/molecules/link-card/link-card.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.ts';
import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';
const getBadgeProps = (commitmentStatus: string, t: TFunction): BadgeProps | undefined => {
  switch (commitmentStatus) {
    case 'Closed':
      {
        return {
          iconBefore: 'alert-triangle',
          label: t('my:sponsorshipRelationship.prior'),
          color: 'warning'
        };
      }
    // TODO: Departing, Pending
  }
};
const useSponsorshipCardData = commitment => {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["children", "my"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    participant
  } = commitment;
  if (!participant) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {};
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const {
    name,
    birthdate,
    country
  } = participant;
  let t1;
  if ($[2] !== birthdate || $[3] !== t) {
    t1 = birthdate ? t("children:sac.age", {
      count: Number(getAgeFromBirthday(birthdate)),
      birthday: birthdate
    }) : undefined;
    $[2] = birthdate;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const birthdayText = t1;
  let t2;
  if ($[5] !== commitment.status || $[6] !== t) {
    t2 = getBadgeProps(commitment.status, t);
    $[5] = commitment.status;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const badgeProps = t2;
  let t3;
  if ($[8] !== badgeProps || $[9] !== birthdayText || $[10] !== country || $[11] !== name) {
    t3 = {
      name,
      country,
      birthdayText,
      badgeProps
    };
    $[8] = badgeProps;
    $[9] = birthdayText;
    $[10] = country;
    $[11] = name;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
export type SponsorshipCardProps = {
  commitment: GraphQLCommitment;
  href: string;
  onClick?: () => void;
};
export const SponsorshipCard: React.FC<SponsorshipCardProps> = t0 => {
  const $ = _c(19);
  const {
    commitment,
    href,
    onClick
  } = t0;
  const {
    name,
    country,
    birthdayText,
    badgeProps
  } = useSponsorshipCardData(commitment);
  let t1;
  if ($[0] !== commitment) {
    t1 = <CommitmentAvatar commitment={commitment} size="xl" />;
    $[0] = commitment;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== badgeProps) {
    t2 = badgeProps && <div className="SponsorshipCard__badge"><Badge responsive={true} {...badgeProps} /></div>;
    $[2] = badgeProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== name) {
    t3 = <div className="SponsorshipCard__title heading-xs md:heading-sm">{name}</div>;
    $[4] = name;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== birthdayText) {
    t4 = birthdayText && <div className="SponsorshipCard__meta flex items-center gap-comfy-xs md:gap-comfy-sm"><div className="flex shrink-0 w-6 h-6"><ArkIcon iconName="birthday-cake" size="1.5rem" /></div><div className="body-sm md:body-md">{birthdayText}</div></div>;
    $[6] = birthdayText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex shrink-0 w-6 h-6"><ArkIcon iconName="globe-05" size="1.5rem" /></div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== country) {
    t6 = <div className="SponsorshipCard__meta flex items-center gap-comfy-xs md:gap-comfy-sm">{t5}<div className="body-sm md:body-md">{country}</div></div>;
    $[9] = country;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== href || $[12] !== onClick || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t6) {
    t7 = <LinkCard className="SponsorshipCard" href={href} onClick={onClick} graphic={t1}>{t2}{t3}{t4}{t6}</LinkCard>;
    $[11] = href;
    $[12] = onClick;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};